import { useEffect, useMemo } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import type { TFunction } from 'i18next';

import { getApiErrorDetail } from '@klareospace/pharos-lib-front-utils';
import type { Vehicle } from '@klareospace/pharos-lib-types';
import { VEHICLE_STATUS } from '@klareospace/pharos-lib-types';

import { createVehicleSchema } from '../schemas/vehicle.schema';
import type { VehicleFormInput } from '../schemas/vehicle.schema';
import { useCreateVehicle } from './use-create-vehicle';
import { useUpdateVehicle } from './use-update-vehicle';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UseVehicleFormReturn {
  form: UseFormReturn<VehicleFormInput>;
  isPending: boolean;
  onSubmit: (data: VehicleFormInput) => Promise<void>;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getFormDefaults(vehicle: Vehicle | null, defaultValues: Partial<VehicleFormInput>): VehicleFormInput {
  if (vehicle) {
    return {
      carrierId: vehicle.carrierId,
      defaultDriverId: vehicle.defaultDriverId ?? '',
      licensePlate: vehicle.licensePlate,
      siteIds: vehicle.siteIds,
      spaceId: vehicle.spaceId,
      status: vehicle.status,
      vehicleTemplateId: vehicle.vehicleTemplateId,
    };
  }
  return {
    carrierId: defaultValues.carrierId ?? '',
    defaultDriverId: '',
    licensePlate: '',
    siteIds: defaultValues.siteIds ?? [],
    spaceId: defaultValues.spaceId ?? '',
    status: VEHICLE_STATUS.AVAILABLE,
    vehicleTemplateId: '',
  };
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useVehicleForm(
  vehicle: Vehicle | null,
  onSuccess: () => void,
  defaultValues?: Partial<VehicleFormInput>,
): UseVehicleFormReturn {
  const { t } = useTranslation('settings');
  const isEditMode = vehicle !== null;

  const createVehicle = useCreateVehicle();
  const updateVehicle = useUpdateVehicle();

  const schema = useMemo(
    () =>
      createVehicleSchema({
        carrierRequired: t('vehicles.form.validation.carrierRequired'),
        licensePlateRequired: t('vehicles.form.validation.licensePlateRequired'),
        spaceRequired: t('vehicles.form.validation.spaceRequired'),
        vehicleTemplateRequired: t('vehicles.form.validation.vehicleTemplateRequired'),
      }),
    [t],
  );

  const form = useForm<VehicleFormInput>({
    defaultValues: getFormDefaults(vehicle, defaultValues ?? {}),
    mode: 'onChange',
    resolver: zodResolver(schema),
  });

  // Reset form only when switching between create/edit mode (vehicle reference changes).
  // defaultValues are contextual (spaceId, carrierId, siteId) and don't change at runtime.
  useEffect(() => {
    form.reset(getFormDefaults(vehicle, defaultValues ?? {}));
  }, [vehicle?.id]);

  async function onSubmit(data: VehicleFormInput): Promise<void> {
    if (createVehicle.isPending || updateVehicle.isPending) return;

    try {
      if (isEditMode) {
        await updateVehicle.mutateAsync({
          body: {
            carrierId: data.carrierId,
            defaultDriverId: data.defaultDriverId || null,
            licensePlate: data.licensePlate.trim(),
            siteIds: data.siteIds,
            status: data.status,
            vehicleTemplateId: data.vehicleTemplateId,
          },
          id: vehicle.id,
        });
      } else {
        await createVehicle.mutateAsync({
          carrierId: data.carrierId,
          defaultDriverId: data.defaultDriverId || undefined,
          licensePlate: data.licensePlate.trim(),
          siteIds: data.siteIds,
          spaceId: data.spaceId,
          status: data.status,
          vehicleTemplateId: data.vehicleTemplateId,
        });
      }
      form.reset();
      onSuccess();
    } catch (error: unknown) {
      handleSubmitError(error, { form, isEditMode, t });
    }
  }

  return {
    form,
    isPending: createVehicle.isPending || updateVehicle.isPending,
    onSubmit,
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

type SubmitErrorContext = {
  form: UseFormReturn<VehicleFormInput>;
  isEditMode: boolean;
  t: TFunction;
};

function handleSubmitError(error: unknown, ctx: SubmitErrorContext): void {
  const detail = getApiErrorDetail(error);

  if (detail.errorCode === 'Conflict.DuplicateLicensePlate') {
    ctx.form.setError('licensePlate', { message: ctx.t('vehicles.form.validation.licensePlateDuplicate') });
    return;
  }

  if (detail.errorCode === 'Conflict.DriverAlreadyAssignedToVehicle') {
    ctx.form.setError('defaultDriverId', { message: ctx.t('vehicles.form.validation.driverAlreadyAssigned') });
    return;
  }

  if (detail.errorCode === 'NotFound.DriverNotFound') {
    ctx.form.setError('defaultDriverId', { message: ctx.t('vehicles.form.validation.driverNotFound') });
    return;
  }

  if (detail.errorCode === 'BadRequest.UserIsNotADriver') {
    ctx.form.setError('defaultDriverId', { message: ctx.t('vehicles.form.validation.userNotDriver') });
    return;
  }

  const fallbackKey = ctx.isEditMode ? 'vehicles.toast.updateError' : 'vehicles.toast.createError';
  toast.error(detail.message || ctx.t(fallbackKey));
}
